import React from 'react';
import {
  BanknotesIcon,
  PhoneXMarkIcon,
  HandRaisedIcon,
  UserMinusIcon,
  NoSymbolIcon,
  ScaleIcon,
  InboxArrowDownIcon,
} from '@heroicons/react/24/outline';

/**
 * La tipificación de una llamada. **Un clic, nunca texto libre.**
 *
 * Es la pieza de producto más importante de la pantalla, y por eso son pocas y
 * no quince. El presupuesto de la jornada es de ~117 s por descolgada,
 * tipificación incluida: 30 s escribiendo × 84 conversaciones son 42 minutos, o
 * sea 14 conversaciones perdidas. Si aquí aparece un `textarea`, la jornada se
 * cae.
 *
 * Se pinta mientras suena la SIGUIENTE llamada: el gestor cierra la anterior en
 * el hueco de los tonos, que es lo que recupera el tiempo de wrap.
 *
 * 🔴 **Describe lo que PASÓ, no lo que viene después.** Hasta el 22/10/2026
 * hacía las dos cosas, y «Volver a llamar» era la única opción que no cerraba el
 * caso: el gestor no elegía lo ocurrido, elegía lo que no daba miedo. Salieron
 * **159 de 173 tipificaciones** con esa opción, el 91,9 %, y «Plan de pagos» no
 * se usó ni una vez. Lo que viene después se decide abajo, en el control de
 * tareas. Ver `docs/llamadas-tipificacion-plan.md`.
 *
 * **«Salta el buzón» y «Cuelga al presentarnos» van separadas a propósito**: la
 * primera dice que NO HABÍA NADIE, la segunda que había alguien y no quiso
 * hablar. Son señales opuestas y juntarlas destruye el único dato interesante.
 */
const OUTCOMES = [
  { value: 'hangs_up_on_intro', label: 'Cuelga al presentarnos', Icon: PhoneXMarkIcon, tone: 'neutral' },
  { value: 'interested', label: 'Interesado en pagar', Icon: BanknotesIcon, tone: 'success' },
  // 🔴 «No va a pagar» y «abre disputa» son dos caminos OPUESTOS, y por eso van
  // separadas: el que discute la deuda deja algo que resolver —documentación
  // que pedirle al cliente—; el que se niega reconociéndola, no. Juntarlas
  // perdía exactamente el dato que decide qué se hace después.
  { value: 'refuses', label: 'Dice que no va a pagar', Icon: HandRaisedIcon, tone: 'warning' },
  { value: 'dispute', label: 'Abre disputa', Icon: ScaleIcon, tone: 'warning' },
  { value: 'wrong_person', label: 'No es la persona', Icon: UserMinusIcon, tone: 'neutral' },
  { value: 'voicemail', label: 'Salta el buzón', Icon: InboxArrowDownIcon, tone: 'neutral' },
];

/**
 * Aparte de las cinco, y en rojo.
 *
 * No es una preferencia de producto: es la única señal de que alguien ha pedido
 * expresamente que no le llamemos. Seguir llamándole es una reclamación.
 */
const DO_NOT_CALL = { value: 'do_not_call', label: 'No llamar más', Icon: NoSymbolIcon };

const TONES = {
  success: 'border-green-200 bg-green-50 text-green-800 hover:bg-green-100',
  neutral: 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50',
  warning: 'border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100',
};

const OutcomeButtons = ({ onSelect, disabled = false, pendingFor = null }) => (
  <div>
    {pendingFor && (
      <p className='mb-2 text-sm text-gray-500'>
        ¿Qué ha pasado con <span className='font-medium text-gray-700'>{pendingFor}</span>?
      </p>
    )}

    <div className='grid grid-cols-2 gap-2 lg:grid-cols-3'>
      {OUTCOMES.map(({ value, label, Icon, tone }) => (
        <button
          key={value}
          type='button'
          disabled={disabled}
          onClick={() => onSelect(value)}
          className={`flex items-center gap-2 rounded-lg border px-3 py-3 text-left text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-40 ${TONES[tone]}`}
        >
          <Icon className='h-5 w-5 shrink-0' aria-hidden='true' />
          <span>{label}</span>
        </button>
      ))}
    </div>

    <button
      type='button'
      disabled={disabled}
      onClick={() => onSelect(DO_NOT_CALL.value)}
      className='mt-2 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-800 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-40'
    >
      <DO_NOT_CALL.Icon className='h-5 w-5 shrink-0' aria-hidden='true' />
      <span>{DO_NOT_CALL.label}</span>
    </button>
  </div>
);

export { OUTCOMES, DO_NOT_CALL };
export default OutcomeButtons;
